import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import {
  LayoutDashboard,
  Receipt,
  Calculator,
  MessageCircle,
  FileText,
  Building,
  Car,
  Users,
  BarChart3,
  Bell,
  Shield,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Sparkles,
  Target,
  Rocket,
  PiggyBank,
  Building2,
  Image,
  Package,
  Wrench,
  HelpCircle,
  Upload,
  FolderUp,
  Bot,
  Heart,
  UserPlus
} from 'lucide-react';

const ONBOARDING_STORAGE_KEY = 'solelytax_onboarding_dismissed';

// Feature Item Component - defined BEFORE use
const FeatureItem = ({ icon: Icon, title, description, color }) => (
  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
    <div className={`p-2 rounded-lg ${color}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <p className="font-medium text-slate-800">{title}</p>
      <p className="text-sm text-slate-600">{description}</p>
    </div>
  </div>
);

// Checklist Item Component - clickable with navigation
const ChecklistItemDisplay = ({ number, title, description, path, onNavigate }) => {
  return (
    <div 
      className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg hover:bg-emerald-50 transition-colors group cursor-pointer"
      onClick={() => onNavigate(path)}
      data-testid={`checklist-item-${number}`}
    >
      <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
        {number}
      </div>
      <div className="flex-1">
        <p className="font-medium text-slate-800 group-hover:text-emerald-700">{title}</p>
        <p className="text-sm text-slate-600">{description}</p>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-500 mt-1" />
    </div>
  );
};

// Welcome Content Component
const WelcomeContent = () => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-3">
      <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
        <PiggyBank className="w-8 h-8 text-emerald-600 mb-2" />
        <p className="font-medium text-emerald-800">Save More</p>
        <p className="text-sm text-emerald-600">AI-powered deduction tracking</p>
      </div>
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
        <Target className="w-8 h-8 text-blue-600 mb-2" />
        <p className="font-medium text-blue-800">Stay Organized</p>
        <p className="text-sm text-blue-600">All finances in one place</p>
      </div>
      <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
        <Sparkles className="w-8 h-8 text-purple-600 mb-2" />
        <p className="font-medium text-purple-800">AI Insights</p>
        <p className="text-sm text-purple-600">Smart tax strategy advice</p>
      </div>
      <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
        <Shield className="w-8 h-8 text-orange-600 mb-2" />
        <p className="font-medium text-orange-800">Audit Ready</p>
        <p className="text-sm text-orange-600">Documentation protection</p>
      </div>
    </div>
  </div>
);

// Core Features Content Component
const CoreFeaturesContent = () => (
  <div className="space-y-3">
    <FeatureItem
      icon={LayoutDashboard}
      title="Dashboard"
      description="Real-time tax health overview with estimated tax liability and savings tracker"
      color="bg-emerald-100 text-emerald-600"
    />
    <FeatureItem
      icon={Receipt}
      title="Ledger"
      description="Track all income and expenses with AI-powered categorization"
      color="bg-blue-100 text-blue-600"
    />
    <FeatureItem
      icon={Calculator}
      title="Deductions"
      description="Track business expenses and see which deductions may apply to your situation"
      color="bg-purple-100 text-purple-600"
    />
    <FeatureItem
      icon={MessageCircle}
      title="AI Advisors"
      description="Get personalized tax strategy advice from AI tax experts"
      color="bg-orange-100 text-orange-600"
    />
  </div>
);

// Tracking Tools Content Component
const TrackingToolsContent = () => (
  <div className="space-y-3">
    <FeatureItem
      icon={Image}
      title="Receipts"
      description="Snap photos of receipts - OCR extracts details automatically"
      color="bg-pink-100 text-pink-600"
    />
    <FeatureItem
      icon={Car}
      title="Mileage Tracker"
      description="Log business miles for vehicle deductions"
      color="bg-cyan-100 text-cyan-600"
    />
    <FeatureItem
      icon={Users}
      title="1099 Contractors"
      description="Track contractor payments for year-end 1099 filing"
      color="bg-indigo-100 text-indigo-600"
    />
    <FeatureItem
      icon={FileText}
      title="Documents"
      description="Store important tax forms and documents securely"
      color="bg-slate-100 text-slate-600"
    />
  </div>
);

// Advanced Tools Content Component
const AdvancedToolsContent = () => (
  <div className="space-y-3">
    <FeatureItem
      icon={Building}
      title="Businesses"
      description="Manage multiple business entities in one place"
      color="bg-amber-100 text-amber-600"
    />
    <FeatureItem
      icon={BarChart3}
      title="Reports"
      description="Generate detailed financial reports and analytics"
      color="bg-green-100 text-green-600"
    />
    <FeatureItem
      icon={Package}
      title="Tax Package"
      description="Create CPA-ready tax packages with one click"
      color="bg-red-100 text-red-600"
    />
    <FeatureItem
      icon={Wrench}
      title="Tools"
      description="S-Corp calculator, scenario planning, and more"
      color="bg-violet-100 text-violet-600"
    />
  </div>
);

// Document Workflow Content Component
const DocumentWorkflowContent = () => (
  <div className="space-y-4">
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <p className="text-sm text-blue-800 font-medium mb-2">
        For users who don't link their bank account:
      </p>
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-xs flex-shrink-0">1</div>
          <div>
            <p className="font-medium text-slate-800">Upload to Document Center</p>
            <p className="text-sm text-slate-600">Go to Documents and upload your bank statements, receipts, or invoices (CSV, PDF, images)</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-xs flex-shrink-0">2</div>
          <div>
            <p className="font-medium text-slate-800">Import to Ledger</p>
            <p className="text-sm text-slate-600">In the Ledger page, click "Import from Documents" to select which files to process</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-xs flex-shrink-0">3</div>
          <div>
            <p className="font-medium text-slate-800">AI Extracts Everything</p>
            <p className="text-sm text-slate-600">Our AI will automatically extract transactions, categorize them, and map to IRS Schedule C line items</p>
          </div>
        </div>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-2 text-center">
      <div className="p-2 bg-slate-50 rounded-lg">
        <Upload className="w-5 h-5 mx-auto text-blue-500 mb-1" />
        <p className="text-xs text-slate-600">Upload</p>
      </div>
      <div className="p-2 bg-slate-50 rounded-lg">
        <FolderUp className="w-5 h-5 mx-auto text-emerald-500 mb-1" />
        <p className="text-xs text-slate-600">Import</p>
      </div>
      <div className="p-2 bg-slate-50 rounded-lg">
        <Sparkles className="w-5 h-5 mx-auto text-purple-500 mb-1" />
        <p className="text-xs text-slate-600">AI Magic</p>
      </div>
    </div>
  </div>
);

// AI Advisors Content Component
const AIAdvisorsContent = () => (
  <div className="space-y-3">
    <p className="text-sm text-slate-600 mb-3">
      Choose from different AI advisor personas, each with unique expertise:
    </p>
    <div className="grid grid-cols-2 gap-3">
      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
        <div className="flex items-center gap-2 mb-1">
          <Bot className="w-5 h-5 text-emerald-600" />
          <p className="font-medium text-emerald-800">Carlotta</p>
        </div>
        <p className="text-xs text-emerald-600">Conservative tax strategist focused on IRS compliance and risk management</p>
      </div>
      <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
        <div className="flex items-center gap-2 mb-1">
          <Bot className="w-5 h-5 text-blue-600" />
          <p className="font-medium text-blue-800">Justin</p>
        </div>
        <p className="text-xs text-blue-600">Tax optimization specialist who helps identify potential deduction opportunities</p>
      </div>
      <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
        <div className="flex items-center gap-2 mb-1">
          <Bot className="w-5 h-5 text-purple-600" />
          <p className="font-medium text-purple-800">Sabrina</p>
        </div>
        <p className="text-xs text-purple-600">Startup specialist with expertise in growth-stage tax strategies</p>
      </div>
      <div className="p-3 bg-orange-50 rounded-lg border border-orange-100">
        <div className="flex items-center gap-2 mb-1">
          <Bot className="w-5 h-5 text-orange-600" />
          <p className="font-medium text-orange-800">David</p>
        </div>
        <p className="text-xs text-orange-600">Balanced advisor offering practical, straightforward guidance</p>
      </div>
    </div>
    <p className="text-xs text-slate-500 mt-2">
      Switch advisors anytime to get different perspectives on your tax situation
    </p>
  </div>
);

// Married Filing Jointly Content Component
const MarriedFilingContent = () => (
  <div className="space-y-4">
    <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Heart className="w-5 h-5 text-pink-600" />
        <p className="font-medium text-pink-800">Filing Jointly with Your Spouse?</p>
      </div>
      <p className="text-sm text-pink-700 mb-3">
        SolelyTax AI supports married filing jointly status with comprehensive spouse income tracking.
      </p>
    </div>
    <div className="space-y-3">
      <FeatureItem
        icon={UserPlus}
        title="Add Spouse Information"
        description="Enter spouse W-2 wages, self-employment income, and withholdings in Settings"
        color="bg-pink-100 text-pink-600"
      />
      <FeatureItem
        icon={FileText}
        title="Upload Spouse Documents"
        description="Upload W-2s and 1099s - AI extracts income and withholding data automatically"
        color="bg-rose-100 text-rose-600"
      />
      <FeatureItem
        icon={Calculator}
        title="Combined Tax Calculations"
        description="See your combined household tax liability and quarterly payment estimates"
        color="bg-red-100 text-red-600"
      />
    </div>
    <p className="text-xs text-slate-500">
      Configure filing status in Settings → Filing Status
    </p>
  </div>
);

// Getting Started Content Component - receives onNavigate prop
const GettingStartedContent = ({ onNavigate }) => (
  <div className="space-y-3">
    <ChecklistItemDisplay 
      number={1} 
      title="Add Your Business" 
      description="Set up your business profile in the Businesses section"
      path="/businesses"
      onNavigate={onNavigate}
    />
    <ChecklistItemDisplay 
      number={2} 
      title="Configure Settings" 
      description="Set your filing status, state, and married filing jointly options"
      path="/settings"
      onNavigate={onNavigate}
    />
    <ChecklistItemDisplay 
      number={3} 
      title="Upload Documents" 
      description="Upload bank statements, receipts, and invoices to the Document Center"
      path="/documents"
      onNavigate={onNavigate}
    />
    <ChecklistItemDisplay 
      number={4} 
      title="Import to Ledger" 
      description="Use 'Import from Documents' in the Ledger to let AI extract transactions"
      path="/transactions"
      onNavigate={onNavigate}
    />
    <ChecklistItemDisplay 
      number={5} 
      title="Chat with AI Advisors" 
      description="Get personalized tax strategy from different AI advisor personas"
      path="/chat"
      onNavigate={onNavigate}
    />
  </div>
);

// Onboarding steps configuration
const onboardingSteps = [
  {
    id: 'welcome',
    title: 'Welcome to SolelyTax AI',
    description: 'Your intelligent tax companion for sole entrepreneurs and startups. Let\'s get you started on maximizing your tax savings.',
    icon: Rocket,
    color: 'bg-emerald-500',
    ContentComponent: WelcomeContent
  },
  {
    id: 'core-features',
    title: 'Core Features',
    description: 'These are the essential tools you\'ll use daily to manage your taxes.',
    icon: LayoutDashboard,
    color: 'bg-blue-500',
    ContentComponent: CoreFeaturesContent
  },
  {
    id: 'document-workflow',
    title: 'Document → Ledger Workflow',
    description: 'The recommended way to import your financial data without linking your bank.',
    icon: Upload,
    color: 'bg-cyan-500',
    ContentComponent: DocumentWorkflowContent
  },
  {
    id: 'ai-advisors',
    title: 'AI Advisor Personas',
    description: 'Meet your team of AI tax advisors with different specialties.',
    icon: Bot,
    color: 'bg-purple-500',
    ContentComponent: AIAdvisorsContent
  },
  {
    id: 'married-filing',
    title: 'Married Filing Jointly',
    description: 'Track spouse income and file jointly with ease.',
    icon: Heart,
    color: 'bg-pink-500',
    ContentComponent: MarriedFilingContent
  },
  {
    id: 'tracking-tools',
    title: 'Tracking & Documentation',
    description: 'Keep everything organized for tax time and potential audits.',
    icon: FileText,
    color: 'bg-indigo-500',
    ContentComponent: TrackingToolsContent
  },
  {
    id: 'advanced-tools',
    title: 'Advanced Tools & Reports',
    description: 'Powerful features for serious tax optimization.',
    icon: Wrench,
    color: 'bg-orange-500',
    ContentComponent: AdvancedToolsContent
  },
  {
    id: 'getting-started',
    title: 'Quick Setup Checklist',
    description: 'Complete these steps to get the most out of SolelyTax AI.',
    icon: CheckCircle,
    color: 'bg-emerald-500',
    ContentComponent: GettingStartedContent
  }
];

const OnboardingModal = ({ forceShow = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (forceShow) {
      setIsOpen(true);
      return;
    }
    
    const dismissed = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!dismissed) {
      setIsOpen(true);
    }
  }, [forceShow]);

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    }
    setIsOpen(false);
  };

  // Handler for checklist item navigation - closes modal and navigates
  const handleChecklistNavigate = (path) => {
    // Save dismissal state if checkbox is checked
    if (dontShowAgain) {
      localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    }
    setIsOpen(false);
    navigate(path);
  };

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleClose();
  };

  const step = onboardingSteps[currentStep];
  const StepIcon = step.icon;
  const ContentComponent = step.ContentComponent;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[600px] p-0 gap-0 overflow-hidden" data-testid="onboarding-modal">
        {/* Header */}
        <div className={`${step.color} p-6 text-white`}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <StepIcon className="w-6 h-6" />
            </div>
            <Badge variant="secondary" className="bg-white/20 text-white hover:bg-white/30">
              Step {currentStep + 1} of {onboardingSteps.length}
            </Badge>
          </div>
          <DialogHeader className="text-left">
            <DialogTitle className="text-2xl font-bold text-white">{step.title}</DialogTitle>
            <DialogDescription className="text-white/90">{step.description}</DialogDescription>
          </DialogHeader>
        </div>

        {/* Content */}
        <div className="p-6">
          <ContentComponent onNavigate={handleChecklistNavigate} />
        </div>

        {/* Footer */}
        <div className="p-6 pt-0 space-y-4">
          {/* Progress Dots */}
          <div className="flex justify-center gap-2">
            {onboardingSteps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentStep(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentStep ? 'bg-emerald-500 w-6' : 'bg-slate-300 hover:bg-slate-400'
                }`}
                data-testid={`onboarding-dot-${idx}`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                id="dont-show"
                checked={dontShowAgain}
                onCheckedChange={setDontShowAgain}
                data-testid="dont-show-again-checkbox"
              />
              <label htmlFor="dont-show" className="text-sm text-slate-600 cursor-pointer">
                Don't show this again
              </label>
            </div>

            <div className="flex items-center gap-2">
              {currentStep > 0 && (
                <Button variant="outline" onClick={handlePrev} data-testid="onboarding-prev-btn">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              )}
              {currentStep === 0 && (
                <Button variant="ghost" onClick={handleSkip} className="text-slate-500" data-testid="onboarding-skip-btn">
                  Skip
                </Button>
              )}
              <Button onClick={handleNext} className="bg-emerald-600 hover:bg-emerald-700" data-testid="onboarding-next-btn">
                {currentStep === onboardingSteps.length - 1 ? (
                  <>
                    Get Started
                    <Rocket className="w-4 h-4 ml-1" />
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Export a function to reset onboarding (useful for testing or re-showing)
export const resetOnboarding = () => {
  localStorage.removeItem(ONBOARDING_STORAGE_KEY);
};

// Export a function to check if onboarding was dismissed
export const isOnboardingDismissed = () => {
  return localStorage.getItem(ONBOARDING_STORAGE_KEY) === 'true';
};

export default OnboardingModal;
